import { Delete, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box, TextField, Button, Tooltip,
} from '@mui/material';
import React from 'react';
import { FormLineItem } from './types';

/**
 * Form line item for vendor statement job
 * @param param0 - The props
 * @returns
*/
// eslint-disable-next-line import/prefer-default-export
export function VendorStatementLineItemRow({
  item,
  index,
  handleLineItemChange,
  removeLineItem,
  toggleHidden,
  viewOnly,
}: {
  item: FormLineItem;
  index: number;
  handleLineItemChange: (index: number, field: string, value: string) => void;
  removeLineItem: (index: number) => void;
  toggleHidden: (index: number) => void;
  viewOnly: boolean;
}) {
  const debit = parseFloat(item.originalInvoiceAmount) || 0;
  const credit = parseFloat(item.amountPaid) || 0;
  const balance = debit - credit;
  return (
    <Box
      key={index}
      sx={{
        mb: 1,
        p: 1,
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        // dim hidden line items (excluded from BigQuery)
        opacity: item.hidden ? 0.5 : 1,
      }}
    >
      {/* Index/line item number box */}
      <Box
        sx={{
          minWidth: '24px',
          height: '24px',
          borderRadius: '50%',
          backgroundColor: 'white',
          color: '#4682B4',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: 'bold',
        }}
      >
        {index + 1}
      </Box>
      {/* Invoice date field */}
      <TextField
        size="small"
        label="* Invoice Date"
        type="date"
        value={item.invoiceDate}
        onChange={(e) => handleLineItemChange(index, 'invoiceDate', e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ width: '160px' }}
        disabled={viewOnly}
      />
      {/* Invoice number field */}
      <TextField
        size="small"
        label="* Invoice #"
        value={item.invoiceNumber}
        onChange={(e) => handleLineItemChange(index, 'invoiceNumber', e.target.value)}
        sx={{ width: '120px' }}
        disabled={viewOnly}
      />
      {/* Description field */}
      <TextField
        size="small"
        label="Description"
        value={item.description}
        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
        sx={{ flex: 1 }}
        disabled={viewOnly}
      />
      {/* PO number field */}
      <TextField
        size="small"
        label="PO #"
        value={item.poNumber}
        onChange={(e) => handleLineItemChange(index, 'poNumber', e.target.value)}
        sx={{ width: '100px' }}
        disabled={viewOnly}
      />
      {/* Original amount field */}
      <TextField
        size="small"
        label="* Original Amount"
        type="number"
        value={item.originalInvoiceAmount}
        onChange={(e) => handleLineItemChange(index, 'originalInvoiceAmount', e.target.value)}
        inputProps={{ step: '0.01' }}
        sx={{ width: '120px' }}
        disabled={viewOnly}
      />
      {/* Amount paid field */}
      <TextField
        size="small"
        label="Amount Paid"
        type="number"
        value={item.amountPaid}
        onChange={(e) => handleLineItemChange(index, 'amountPaid', e.target.value)}
        inputProps={{ step: '0.01' }}
        sx={{ width: '120px' }}
        disabled={viewOnly}
      />
      {/* Balance field */}
      <TextField
        size="small"
        label="Balance"
        type="number"
        value={balance.toString()}
        inputProps={{ step: '0.01' }}
        sx={{ width: '120px' }}
        disabled
      />
      {/* Remove line item button */}
      {!viewOnly && (
      <Button
        size="small"
        color="error"
        onClick={() => removeLineItem(index)}
        sx={{ minWidth: 'auto', px: 1 }}
      >
        <Delete />
      </Button>
      )}
      {/* Hide/unhide line item button - only on completed (view only) statements */}
      {viewOnly && (
      <Tooltip title={item.hidden ? 'Unhide (include in BigQuery)' : 'Hide (exclude from BigQuery)'}>
        <Button
          size="small"
          onClick={() => toggleHidden(index)}
          sx={{ minWidth: 'auto', px: 1, color: '#4682B4' }}
        >
          {item.hidden ? <VisibilityOff /> : <Visibility />}
        </Button>
      </Tooltip>
      )}
    </Box>
  );
}
